import { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, Calendar, Download, RefreshCw, AlertTriangle,
  CheckCircle, Clock, XCircle, ChevronRight, Zap, Shield,
  ArrowUpRight, Trash2, Star, Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { InvoiceDialog, UsageMeter, INVOICE_STATUS, money } from './BillingInvoice';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('backoffice_token');

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// Status configuration
const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, description: 'Your subscription is active' },
  trialing: { label: 'Trial', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, description: 'You are on a free trial' },
  past_due: { label: 'Past Due', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, description: 'Payment failed - please update your payment method' },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle, description: 'Your subscription has been canceled' },
  none: { label: 'No Subscription', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle, description: 'No active subscription' }
};

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-');
const isOpen = (inv) => inv.status === 'open' || inv.status === 'past_due' || inv.status === 'failed';

export default function BillingPage() {
  const isMobile = useIsMobile();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteCardDialog, setShowDeleteCardDialog] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState(null);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [usage, setUsage] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);


  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch subscription, plans, payment methods, and invoices from Billing V2 API
      const [subRes, plansRes, pmRes, invoicesRes, usageRes] = await Promise.all([
        fetch(`${API}/api/billing/v2/me/subscription`, { headers: getHeaders() }),
        fetch(`${API}/api/billing/v2/plans`),
        fetch(`${API}/api/billing/v2/payment-methods`, { headers: getHeaders() }),
        fetch(`${API}/api/billing/v2/invoices?limit=24&days=365`, { headers: getHeaders() }),
        fetch(`${API}/api/billing/v2/usage`, { headers: getHeaders() })
      ]);
      const usageData = usageRes.ok ? await usageRes.json() : { usage: null };
      setUsage(usageData.usage || null);

      const subData = subRes.ok ? await subRes.json() : { has_subscription: false };
      const plansData = plansRes.ok ? await plansRes.json() : { plans: [] };
      const pmData = pmRes.ok ? await pmRes.json() : { payment_methods: [] };
      const invoicesData = invoicesRes.ok ? await invoicesRes.json() : { invoices: [] };

      setSubscriptionData(subData);
      setPlans(plansData.plans || []);
      setPaymentMethods(pmData.payment_methods || []);
      setInvoices(invoicesData.invoices || []);
      
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Back from the hosted card page: read the card it saved, then load.
    const params = new URLSearchParams(window.location.search);
    if (params.get('card') === 'saved') {
      params.delete('card');
      const rest = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
      (async () => {
        try {
          const res = await fetch(`${API}/api/billing/v2/payment-methods/sync`, { method: 'POST', headers: getHeaders() });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Could not read the saved card');
          const card = (data.payment_methods || []).find((m) => m.is_default) || (data.payment_methods || [])[0];
          toast.success(card ? `Card ending ${card.last4} saved for autopay` : 'No card was saved');
          (data.charged_invoices || []).forEach((inv) => {
            if (inv.status === 'paid') toast.success(`Invoice ${inv.number} paid`);
            else toast.error(`Invoice ${inv.number}: ${inv.failure_reason || 'payment failed'}`);
          });
        } catch (err) {
          toast.error(err.message);
        } finally {
          fetchData();
        }
      })();
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleStartSubscription = async () => {
    if (!selectedPlanCode) return;
    if (!paymentMethods.length) {
      toast.error('Add a card on file first: your plan is paid by autopay from that card.');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/subscription/start`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ 
          plan_code: selectedPlanCode,
          trial_days: 14
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to start subscription');
      }

      toast.success(data.message || 'Subscription started successfully');
      setShowPlanDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlanCode) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/subscription/change-plan`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ new_plan_code: selectedPlanCode })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to change plan');
      }

      toast.success(data.message || 'Plan changed successfully');
      setShowPlanDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (atPeriodEnd = true) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/subscription/cancel`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ at_period_end: atPeriodEnd })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to cancel subscription');
      }

      toast.success(data.message || 'Subscription canceled');
      setShowCancelDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/subscription/reactivate`, {
        method: 'POST',
        headers: getHeaders()
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to reactivate subscription');
      }

      toast.success(data.message || 'Subscription reactivated');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({})
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Payment failed');
      }

      if (data.ok && data.invoice?.status === 'paid') {
        toast.success('Payment successful');
        setViewInvoice(null);
      } else {
        toast.error(data.message || 'Payment failed');
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/payment-methods/tokenize`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          // the card page sends the owner back here; ?card=saved reads the new card
          return_url: `${window.location.origin}${window.location.pathname}?card=saved`
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to create payment session');
      }

      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      } else {
        throw new Error('No payment URL returned');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncPaymentMethods = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/payment-methods/sync`, {
        method: 'POST',
        headers: getHeaders()
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to sync payment methods');
      }

      toast.success(`Synced ${data.synced_count} payment method(s)`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!cardToDelete) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/payment-methods/${cardToDelete}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to remove card');
      }

      toast.success('Card removed successfully');
      setShowDeleteCardDialog(false);
      setCardToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetDefaultCard = async (pmId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/v2/payment-methods/set-default`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ payment_method_id: pmId })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to set default card');
      }

      toast.success('Default card updated');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-4 sm:p-6 text-center">
          <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-red-800">Billing Service Unavailable</h3>
          <p className="text-sm sm:text-base text-red-600 mt-2">{error}</p>
          <Button onClick={fetchData} className="mt-4 min-h-[44px]">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const status = subscriptionData?.status || 'none';
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const StatusIcon = statusConfig.icon;
  const currentPlan = subscriptionData?.plan;
  const hasSubscription = subscriptionData?.has_subscription;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="billing-page">
      {/* Header - Responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 md:text-2xl">Billing & Subscription</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your subscription and payment methods</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="min-h-[40px] w-full sm:w-auto">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Past Due Banner */}
      {status === 'past_due' && (
        <Card className="bg-red-50 border-red-200" data-testid="past-due-banner">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-red-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium text-red-800">Payment failed</h3>
                <p className="text-sm text-red-600 mt-1">
                  Your last autopay charge did not go through. Add another card or pay the open invoice
                  {subscriptionData?.grace_ends_at ? ` before ${day(subscriptionData.grace_ends_at)}` : ''} to keep your account in good standing.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" onClick={handleAddPaymentMethod} disabled={actionLoading}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Add a card
                  </Button>
                  {invoices.filter(isOpen).slice(0, 1).map((inv) => (
                    <Button key={inv.id} size="sm" variant="outline" onClick={() => setViewInvoice(inv.id)} data-testid="past-due-view-invoice">
                      View invoice {inv.number}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial Expiring Banner */}
      {status === 'trialing' && subscriptionData?.is_trial_expired && (
        <Card className="bg-amber-50 border-amber-200" data-testid="trial-expired-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Clock className="h-6 w-6 text-amber-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800">Trial Expired</h3>
                <p className="text-sm text-amber-600 mt-1">
                  Your trial has ended. Add a payment method to continue using the service.
                </p>
                <Button size="sm" className="mt-3" onClick={handleAddPaymentMethod} disabled={actionLoading}>
                  Add Payment Method
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Notice */}
      {subscriptionData?.cancel_at_period_end && status !== 'canceled' && (
        <Card className="bg-amber-50 border-amber-200" data-testid="cancel-notice">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Clock className="h-6 w-6 text-amber-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800">Subscription Ending</h3>
                <p className="text-sm text-amber-600 mt-1">
                  Your subscription will end on {subscriptionData?.period_end ? new Date(subscriptionData.period_end).toLocaleDateString() : 'the end of your billing period'}.
                </p>
                <Button size="sm" variant="outline" className="mt-3" onClick={handleReactivate} disabled={actionLoading}>
                  Keep My Subscription
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Subscription Card */}
        <Card className="lg:col-span-2" data-testid="subscription-card">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg">Current Subscription</CardTitle>
                <CardDescription className="text-xs sm:text-sm">{statusConfig.description}</CardDescription>
              </div>
              <Badge className={cn(statusConfig.color, "w-fit mt-1 sm:mt-0")}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-6">
            {hasSubscription && currentPlan ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg gap-3">
                  <div>
                    <p className="font-semibold text-base sm:text-lg">{currentPlan.name} Plan</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">
                      ${currentPlan.price_dollars?.toFixed(2) || (currentPlan.price_cents / 100).toFixed(2)}
                      <span className="text-sm font-normal text-gray-500">/{currentPlan.interval}</span>
                    </p>
                  </div>
                  {(status === 'active' || status === 'trialing') && (
                    <Button variant="outline" onClick={() => { setSelectedPlanCode(null); setShowPlanDialog(true); }} className="w-full sm:w-auto min-h-[44px]">
                      Change Plan
                    </Button>
                  )}
                </div>

                {/* Plan Features */}
                {subscriptionData?.features?.length > 0 && (
                  <div className="p-3 sm:p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800 mb-2 text-sm sm:text-base">Included Features</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {subscriptionData.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-blue-700">
                          <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="truncate">{feature.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Period Info */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-500">Period Start</p>
                    <p className="font-medium text-sm sm:text-base">
                      {subscriptionData?.period_start ? new Date(subscriptionData.period_start).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-500">
                      {status === 'trialing' ? 'Trial Ends' : 'Renewal'}
                    </p>
                    <p className="font-medium text-sm sm:text-base">
                      {subscriptionData?.period_end ? new Date(subscriptionData.period_end).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>

                <div className="p-3 sm:p-4 rounded-lg border text-xs sm:text-sm" data-testid="autopay-note">
                  {subscriptionData?.next_charge_at ? (
                    subscriptionData?.has_card ? (
                      <p>Autopay: <b>{day(subscriptionData.next_charge_at)}</b>, one day before the due date, to the {paymentMethods.find((m) => m.is_default)?.brand || 'card'} ending {paymentMethods.find((m) => m.is_default)?.last4}. The invoice lists the plan and any texts or AI requests over the plan.</p>
                    ) : (
                      <p className="text-red-600">No card on file: add one so autopay can pay the invoice due {day(subscriptionData.period_end)}.</p>
                    )
                  ) : (
                    <p>No further charges are scheduled.</p>
                  )}
                </div>

                {usage && (
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium text-sm sm:text-base mb-2">Usage this period</p>
                    <UsageMeter usage={usage} />
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                  {(status === 'active' || status === 'trialing') && !subscriptionData?.cancel_at_period_end && (
                    <Button variant="outline" className="text-red-600 hover:bg-red-50 min-h-[44px] w-full sm:w-auto" onClick={() => setShowCancelDialog(true)}>
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No active subscription</p>
                {!paymentMethods.length && (
                  <p className="text-xs text-gray-500 mt-1">A plan needs a card on file for autopay. Add the card first, then choose the plan.</p>
                )}
                <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
                  {!paymentMethods.length && (
                    <Button variant="outline" onClick={handleAddPaymentMethod} disabled={actionLoading} data-testid="add-card-first">
                      <CreditCard className="h-4 w-4 mr-2" />Add a card
                    </Button>
                  )}
                  <Button onClick={() => setShowPlanDialog(true)} data-testid="choose-plan">
                    Choose a Plan
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods Card */}
        <Card data-testid="payment-method-card">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="truncate">Payment Methods</span>
              </CardTitle>
              <div className="flex gap-1 flex-shrink-0">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleSyncPaymentMethods} 
                  disabled={actionLoading}
                  title="Refresh saved cards"
                  className="min-w-[36px] min-h-[36px]"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleAddPaymentMethod} 
                  disabled={actionLoading}
                  className="min-h-[36px]"
                >
                  <Plus className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Add</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {paymentMethods.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {paymentMethods.map((pm) => (
                  <div 
                    key={pm.id} 
                    className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border ${
                      pm.is_default ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className={`h-8 w-12 sm:h-10 sm:w-14 rounded flex items-center justify-center flex-shrink-0 ${
                      pm.brand === 'VISA' ? 'bg-blue-600' :
                      pm.brand === 'MASTERCARD' ? 'bg-orange-500' :
                      pm.brand === 'AMEX' ? 'bg-blue-800' :
                      'bg-gray-600'
                    }`}>
                      <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <p className="font-medium text-sm sm:text-base truncate">{pm.brand || 'Card'} •••• {pm.last4}</p>
                        {pm.is_default && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Default</Badge>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500">Exp {pm.expire || `${pm.exp_month}/${pm.exp_year}`}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!pm.is_default && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSetDefaultCard(pm.id)}
                          disabled={actionLoading}
                          title="Set as default"
                          className="min-w-[36px] min-h-[36px]"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCardToDelete(pm.id);
                          setShowDeleteCardDialog(true);
                        }}
                        disabled={actionLoading}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 min-w-[36px] min-h-[36px]"
                        title="Remove card"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 sm:py-6">
                <CreditCard className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-xs sm:text-sm text-gray-500 mb-4">No payment methods on file</p>
                <Button variant="outline" className="w-full min-h-[44px]" onClick={handleAddPaymentMethod} disabled={actionLoading}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoices - Responsive */}
      <Card data-testid="invoices-card">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Invoices</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Every invoice itemized: the plan, then texts and AI requests over the plan</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length > 0 ? (
            isMobile ? (
              // Mobile Invoice Cards
              <div className="divide-y divide-gray-100">
                {invoices.map((invoice) => {
                  const invStatus = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.open;
                  return (
                    <div key={invoice.id} className="p-4" data-testid={`invoice-card-${invoice.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-gray-500">{invoice.number}</p>
                          <p className="font-semibold text-lg">${invoice.amount_dollars?.toFixed(2) || (invoice.amount_cents / 100).toFixed(2)}</p>
                        </div>
                        <Badge className={invStatus.color}>{invStatus.label}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">Due {day(invoice.due_at || invoice.created_at)}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setViewInvoice(invoice.id)} className="min-h-[36px]" data-testid={`invoice-view-${invoice.number}`}>
                            View
                          </Button>
                          {isOpen(invoice) && (
                            <Button 
                              size="sm" 
                              onClick={() => handlePayInvoice(invoice.id)}
                              disabled={actionLoading}
                              className="min-h-[36px]"
                            >
                              Pay Now
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Desktop Table
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const invStatus = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.open;
                    return (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-mono text-sm">{invoice.number}</TableCell>
                        <TableCell>{day(invoice.due_at || invoice.created_at)}</TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-[280px]">
                          {(invoice.lines || []).filter((ln) => ln.amount_cents > 0).map((ln) => (
                            <div key={ln.id}>{ln.kind === 'plan' ? (invoice.plan_name || 'Plan') : ln.kind === 'sms_overage' ? `${ln.quantity.toLocaleString()} extra texts` : `${ln.quantity.toLocaleString()} extra AI requests`}: {money(ln.amount_cents)}</div>
                          ))}
                        </TableCell>
                        <TableCell>{money(invoice.amount_cents)}</TableCell>
                        <TableCell>
                          <Badge className={invStatus.color}>{invStatus.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2 whitespace-nowrap">
                          <Button variant="outline" size="sm" onClick={() => setViewInvoice(invoice.id)} data-testid={`invoice-view-${invoice.number}`}>
                            View
                          </Button>
                          {isOpen(invoice) && (
                            <Button 
                              size="sm" 
                              onClick={() => handlePayInvoice(invoice.id)}
                              disabled={actionLoading}
                            >
                              Pay Now
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          ) : (
            <div className="text-center py-8 sm:py-10 text-gray-500">
              <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm sm:text-base">No invoices yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Selection Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{hasSubscription ? 'Change Plan' : 'Choose a Plan'}</DialogTitle>
            <DialogDescription className="text-sm">Select the plan that best fits your needs</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 py-4">
            {plans.filter(p => p.is_active).map((plan) => {
              const isCurrentPlan = plan.code === currentPlan?.code;
              const isSelected = plan.code === selectedPlanCode;
              
              return (
                <div
                  key={plan.code}
                  className={`p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected ? 'border-blue-500 bg-blue-50' : 
                    isCurrentPlan ? 'border-green-500 bg-green-50' : 
                    'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => !isCurrentPlan && setSelectedPlanCode(plan.code)}
                  data-testid={`plan-option-${plan.code}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-base sm:text-lg">{plan.name}</h3>
                    {isCurrentPlan && <Badge className="bg-green-100 text-green-700 text-xs">Current</Badge>}
                  </div>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 mb-3">
                    ${plan.price_dollars || (plan.price_cents / 100)}
                    <span className="text-sm font-normal text-gray-500">/{plan.interval}</span>
                  </p>
                  <p className="text-xs text-gray-600 mb-2" data-testid={`plan-allowance-${plan.code}`}>
                    {(plan.sms_included || 0).toLocaleString()} texts and {(plan.ai_included || 0).toLocaleString()} AI requests a month included;
                    then {money(plan.sms_overage_cents)} per text, {money(plan.ai_overage_cents)} per AI request
                  </p>
                  <ul className="space-y-1 sm:space-y-2">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="capitalize truncate">{feature.replace(/_/g, ' ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="min-h-[44px] w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={hasSubscription ? handleChangePlan : handleStartSubscription}
              disabled={!selectedPlanCode || actionLoading || selectedPlanCode === currentPlan?.code}
              className="min-h-[44px] w-full sm:w-auto"
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {hasSubscription ? 'Change Plan' : 'Start 14-day trial'}
            </Button>
          </DialogFooter>
          {!hasSubscription && (
            <p className="text-xs text-gray-500">
              {paymentMethods.length
                ? 'Free for 14 days. Autopay then charges your card on file one day before each due date.'
                : 'Add a card on file first: the plan is paid by autopay from that card.'}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">What happens when you cancel:</p>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    <li>• Your access continues until {subscriptionData?.period_end ? new Date(subscriptionData.period_end).toLocaleDateString() : 'the end of your billing period'}</li>
                    <li>• You won't be charged again</li>
                    <li>• You can reactivate anytime</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleCancel(true)}
              disabled={actionLoading}
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Cancel at Period End
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceDialog invoiceId={viewInvoice} onClose={() => setViewInvoice(null)} onPay={handlePayInvoice} paying={actionLoading} />

      {/* Delete Card Confirmation Dialog */}
      <AlertDialog open={showDeleteCardDialog} onOpenChange={setShowDeleteCardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this card? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCardToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCard}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Remove Card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
